import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { baseApi } from '../../app/api/baseApi'
import { accountApi } from '../account/accountApi'
export const AUTH_STATUS = {
    CHECKING: 'checking',
    AUTHENTICATED: 'authenticated',
    ANONYMOUS: 'anonymous'
}
const toUser = (cognitoUser) => {
    const session = cognitoUser && cognitoUser.getSignInUserSession && cognitoUser.getSignInUserSession()
    const payload = (session && session.getIdToken().payload) || {}
    return {
        username: cognitoUser.username || '',
        name: payload.name || '',
        email: payload.email || '',
        groups: payload['cognito:groups'] || []
    }
}
const isProvisioned = (user) => user.groups.length > 0
const currentUser = () => Auth.currentAuthenticatedUser().catch(() => null)
const refreshSession = (cognitoUser) => new Promise((resolve, reject) => {
    const refreshToken = cognitoUser.getSignInUserSession().getRefreshToken()
    cognitoUser.refreshSession(refreshToken, (error, session) => (error ? reject(error) : resolve(session)))
})

export const restoreSession = createAsyncThunk('auth/restoreSession', async () => {
    const cognitoUser = await currentUser()
    if (!cognitoUser) {
        return null
    }
    const user = toUser(cognitoUser)
    return isProvisioned(user) ? user : null
})

export const completeSignIn = createAsyncThunk('auth/completeSignIn', async (_, { dispatch }) => {
    const cognitoUser = await currentUser()
    if (!cognitoUser) {
        return { user: null, isFirstLogin: false }
    }
    let user = toUser(cognitoUser)
    if (isProvisioned(user)) {
        return { user, isFirstLogin: false }
    }
    try {
        const client = await dispatch(accountApi.endpoints.registerClient.initiate()).unwrap()
        await refreshSession(cognitoUser)
        user = toUser(await currentUser())
        return { user, isFirstLogin: Boolean(client && client.isFirstLogin && client.result) }
    } catch (error) {
        await Auth.signOut()
        throw error
    }
})
export const handleSignedOut = () => (dispatch) => {
    dispatch(baseApi.util.resetApiState())
    dispatch(authSlice.actions.signedOut())
}

const initialState = {
    status: AUTH_STATUS.CHECKING,
    user: null,
    isFirstLogin: false
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        signedOut: () => ({ ...initialState, status: AUTH_STATUS.ANONYMOUS })
    },
    extraReducers: (builder) => {
        builder
            .addCase(restoreSession.fulfilled, (state, action) => {
                if (state.status === AUTH_STATUS.AUTHENTICATED) {
                    return
                }
                state.user = action.payload
                state.status = action.payload ? AUTH_STATUS.AUTHENTICATED : AUTH_STATUS.ANONYMOUS
            })
            .addCase(restoreSession.rejected, (state) => {
                state.status = AUTH_STATUS.ANONYMOUS
            })
            .addCase(completeSignIn.fulfilled, (state, action) => {
                if (!action.payload.user) {
                    return
                }
                state.user = action.payload.user
                state.isFirstLogin = action.payload.isFirstLogin
                state.status = AUTH_STATUS.AUTHENTICATED
            })
    }
})

export const selectAuthStatus = (state) => state.auth.status
export const selectCurrentUser = (state) => state.auth.user
export const selectIsFirstLogin = (state) => state.auth.isFirstLogin

export default authSlice.reducer
